import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Logo, DeviceSwitcher, PreviewPlaceholder } from '../../../../components/wizard';
import { usePreviewStage, useDeviceAnchor } from '../../../../components/preview-stage';
import { FillIframe } from '../../../../components/preview-stage/PreviewStage.styles';
import { useConfigurator } from '../../../../store/configurator';
import { useIsTablet } from '../../../../lib/deviceType';
import { useIframeReveal } from '../../../../lib/iframePreview';
import { Backdrop, Popup, TopBar, SwitcherSlot, CloseButton, CloseLabel, Body, Clip } from './PreviewOverlay.styles';

/**
 * Full-screen in-app live preview. A white top bar (logo · Mobile/Desktop switch ·
 * Close) over the casino rendered either full-width (desktop) or in a centred phone
 * column (mobile). Closes on Escape or backdrop click.
 *
 * **Desktop:** it owns no iframe. It renders only its chrome + an empty full-screen
 * ANCHOR, portalled INTO the persistent {@link PreviewStage} host's layer (so they share
 * a stacking context). The host floats the SAME warm, unified morph device the build
 * steps use, bare above the blurred backdrop. That device already carries whatever
 * route/session state the user reached while configuring, so opening Live Preview shows
 * exactly that. Toggling the in-overlay switch morphs that same device; zero reload.
 *
 * **Tablet:** the persistent host is suppressed (its morph iframe re-measures into an
 * infinite-update crash on iPad Safari), so there is nothing to float. Instead the
 * overlay mounts its OWN transient iframe, created only while open and destroyed on
 * close, exactly like the mobile flow's live-preview escape hatch. A single plain iframe
 * has none of the morph device's measurement machinery, so it can't hit that crash; the
 * Mobile/Desktop switch simply changes the column width (the framed site reflows itself).
 */
export const PreviewOverlay = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { fullscreenOpen, fullscreenDevice, setFullscreenDevice, closeFullscreen, containerNode } =
    usePreviewStage();
  const { previewUrl, selectedColor } = useConfigurator();

  // Unified: floats the morph device, not a role (mac/phone) iframe. `role` is ignored for
  // unified anchors (see AnchorSpec), so it's fixed at 'mac' by convention; the bezel/width
  // the overlay shows follows `fullscreenDevice` inside PreviewStageView, not this
  // registration. Registered unconditionally so the hook order stays stable; inert on
  // tablet (the host that reads it is absent there).
  const anchorRef = useDeviceAnchor({ role: 'mac', mode: 'fullscreen', bezel: 'none', unified: true });

  // Tablet-only live iframe. Its src is empty (so no site loads) unless the overlay is both
  // a tablet AND open; the reveal gate re-hides it on each open until it paints. `previewUrl`
  // itself is unchanged by a device switch, so switching only resizes the column and never
  // reloads (nor re-covers) the frame.
  const tabletSrc = isTablet && fullscreenOpen ? previewUrl : '';
  const reveal = useIframeReveal(tabletSrc);

  React.useEffect(() => {
    if (!fullscreenOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreenOpen, closeFullscreen]);

  // Desktop shares the host's layer node (so the floated device sits under this chrome);
  // tablet has no host, so it portals into <body> like the mobile live preview.
  const portalTarget = isTablet ? document.body : containerNode;
  if (!fullscreenOpen || !portalTarget) return null;

  return createPortal(
    <Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) closeFullscreen();
      }}
    >
      <Popup>
        <TopBar>
          <Logo dark />
          <SwitcherSlot>
            <DeviceSwitcher value={fullscreenDevice} onChange={setFullscreenDevice} />
          </SwitcherSlot>
          <CloseButton type="button" data-ga-id="cfg-finish-preview-close" onClick={closeFullscreen}>
            <CloseLabel text="Close Preview" tag="span" />
          </CloseButton>
        </TopBar>

        <Body>
          {isTablet ? (
            <Clip $mobile={fullscreenDevice === 'mobile'}>
              {tabletSrc ? (
                <FillIframe
                  ref={reveal.iframeRef}
                  title={t('Site preview')}
                  src={tabletSrc}
                  onLoad={reveal.onLoad}
                  $shown={reveal.revealed}
                />
              ) : null}
              <PreviewPlaceholder shown={!reveal.revealed} bgColor={selectedColor?.bg} />
            </Clip>
          ) : (
            <Clip ref={anchorRef} $mobile={fullscreenDevice === 'mobile'} />
          )}
        </Body>
      </Popup>
    </Backdrop>,
    portalTarget,
  );
};

export default PreviewOverlay;
